import React, { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { supabase } from '../lib/supabase'
import { DashboardLayout } from '../components/Layout'
import { Button, Badge, Modal, Input, Textarea, Select, toast, Spinner, Avatar } from '../components/UI'
import {
  MapPin, Eye, Ruler, Zap, Sun, Calendar, DollarSign,
  Heart, Share2, ArrowLeft, CheckCircle, Building2, Info, Camera
} from 'lucide-react'
import { format, differenceInDays, addDays } from 'date-fns'

function ImageGallery({ photos }) {
  const [main, setMain] = useState(0)
  const imgs = photos?.length > 0
    ? photos
    : ['https://images.unsplash.com/photo-1617550523898-b2b67b3d5b16?w=800&q=80']

  return (
    <div>
      <div style={{ aspectRatio: '16/9', borderRadius: 'var(--radius-lg)', overflow: 'hidden', background: 'var(--gray-200)', marginBottom: 10 }}>
        <img src={imgs[main]} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
      </div>
      {imgs.length > 1 && (
        <div style={{ display: 'flex', gap: 8, overflowX: 'auto' }}>
          {imgs.map((img, i) => (
            <div key={i} onClick={() => setMain(i)} style={{
              width: 80, height: 56, borderRadius: 8, overflow: 'hidden', cursor: 'pointer', flexShrink: 0,
              border: `2px solid ${i === main ? 'var(--tech)' : 'transparent'}`, transition: 'border-color 0.15s'
            }}>
              <img src={img} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

function BookingModal({ space, isOpen, onClose }) {
  const { profile } = useAuth()
  const navigate = useNavigate()
  const [form, setForm] = useState({
    start_date: format(addDays(new Date(), 1), 'yyyy-MM-dd'),
    end_date: format(addDays(new Date(), 31), 'yyyy-MM-dd'),
    campaign_name: '', brand_name: profile?.company_name || '', advertiser_notes: '',
  })
  const [loading, setLoading] = useState(false)

  const days = differenceInDays(new Date(form.end_date), new Date(form.start_date))
  const pricePerDay = space?.price_per_month ? space.price_per_month / 30 : 0
  const basePrice = Math.round(pricePerDay * days)
  const commission = Math.round(basePrice * (space?.commission_rate || 10) / 100)
  const total = basePrice + commission
  const ownerPayout = basePrice

  async function handleSubmit() {
    if (days < (space?.minimum_booking_days || 7)) {
      toast.error(`Minimum booking is ${space.minimum_booking_days || 7} days`)
      return
    }
    setLoading(true)
    try {
      const { data, error } = await supabase.from('bookings').insert({
        ad_space_id: space.id,
        advertiser_id: profile.id,
        owner_id: space.owner_id,
        start_date: form.start_date,
        end_date: form.end_date,
        base_price: basePrice,
        commission_amount: commission,
        production_fee: space.production_fee || 0,
        total_amount: total,
        owner_payout: ownerPayout,
        currency: 'AED',
        campaign_name: form.campaign_name,
        brand_name: form.brand_name,
        advertiser_notes: form.advertiser_notes,
        status: 'inquiry',
      }).select().single()

      if (error) throw error
      toast.success('Booking request sent! The media owner will review it.')
      onClose()
      navigate(`/bookings/${data.id}`)
    } catch (e) {
      toast.error(e.message || 'Failed to submit booking')
    } finally {
      setLoading(false)
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Request Booking" size="md"
      footer={
        <>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSubmit} loading={loading} icon={<CheckCircle size={16} />}>
            Send Booking Request
          </Button>
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {/* Space summary */}
        <div style={{ background: 'var(--gray-50)', borderRadius: 'var(--radius)', padding: 14, display: 'flex', gap: 12 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, marginBottom: 2 }}>{space?.title}</div>
            <div style={{ fontSize: '0.82rem', color: 'var(--gray-500)', display: 'flex', gap: 6 }}>
              <MapPin size={12} /> {space?.area}, Dubai
            </div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontWeight: 800, color: 'var(--navy)' }}>{space?.price_per_month?.toLocaleString()} AED</div>
            <div style={{ fontSize: '0.75rem', color: 'var(--gray-400)' }}>/month</div>
          </div>
        </div>

        <div className="grid-2">
          <Input label="Start Date" type="date" value={form.start_date}
            onChange={e => setForm(p => ({ ...p, start_date: e.target.value }))} required
            hint={`Min ${space?.minimum_booking_days || 7} days`} />
          <Input label="End Date" type="date" value={form.end_date}
            onChange={e => setForm(p => ({ ...p, end_date: e.target.value }))} required />
        </div>

        <Input label="Brand / Campaign Name" placeholder="e.g. Summer Sale 2026" value={form.campaign_name}
          onChange={e => setForm(p => ({ ...p, campaign_name: e.target.value }))} required />
        <Input label="Your Company Name" value={form.brand_name}
          onChange={e => setForm(p => ({ ...p, brand_name: e.target.value }))} />
        <Textarea label="Additional Notes" placeholder="Any specific requirements or questions..." value={form.advertiser_notes}
          onChange={e => setForm(p => ({ ...p, advertiser_notes: e.target.value }))} />

        {/* Price breakdown */}
        {days > 0 && (
          <div style={{ background: 'var(--navy)', color: 'white', borderRadius: 'var(--radius)', padding: 16 }}>
            <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, marginBottom: 12, color: 'rgba(255,255,255,0.7)', fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              Price Breakdown
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {[
                [`${days} days @ ${Math.round(pricePerDay).toLocaleString()} AED/day`, `${basePrice.toLocaleString()} AED`],
                [`AdsLocate fee (${space?.commission_rate || 10}%)`, `${commission.toLocaleString()} AED`],
                space?.production_fee > 0 ? [`Production fee`, `${space.production_fee.toLocaleString()} AED`] : null,
              ].filter(Boolean).map(([label, value], i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.88rem', color: 'rgba(255,255,255,0.7)' }}>
                  <span>{label}</span><span>{value}</span>
                </div>
              ))}
              <div style={{ borderTop: '1px solid rgba(255,255,255,0.15)', paddingTop: 8, display: 'flex', justifyContent: 'space-between', fontWeight: 800, fontSize: '1.05rem' }}>
                <span>Total</span><span style={{ color: 'var(--accent)' }}>{total.toLocaleString()} AED</span>
              </div>
            </div>
            <p style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.4)', marginTop: 8 }}>
              Payment is arranged directly with the media owner after approval. AdsLocate earns a 10% commission.
            </p>
          </div>
        )}
      </div>
    </Modal>
  )
}

export default function SpaceDetailPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { user, isAdvertiser } = useAuth()
  const [space, setSpace] = useState(null)
  const [owner, setOwner] = useState(null)
  const [loading, setLoading] = useState(true)
  const [showBooking, setShowBooking] = useState(false)
  const [saved, setSaved] = useState(false)

  useEffect(() => {
    async function load() {
      const { data } = await supabase.from('ad_spaces').select('*').eq('id', id).single()
      if (data) {
        setSpace(data)
        const { data: ownerData } = await supabase.from('profiles').select('*').eq('id', data.owner_id).single()
        setOwner(ownerData)
      }
      setLoading(false)
    }
    load()
  }, [id])

  if (loading) return (
    <DashboardLayout><div style={{ display: 'flex', justifyContent: 'center', padding: 80 }}><Spinner size={40} /></div></DashboardLayout>
  )

  if (!space) return (
    <DashboardLayout>
      <div style={{ textAlign: 'center', padding: 80 }}>
        <h2>Space not found</h2>
        <Button onClick={() => navigate('/explore')} style={{ marginTop: 16 }}>Back to Explore</Button>
      </div>
    </DashboardLayout>
  )

  const specsData = [
    space.width_meters && space.height_meters ? { icon: <Ruler size={16} />, label: 'Dimensions', value: `${space.width_meters}m × ${space.height_meters}m` } : null,
    { icon: <Eye size={16} />, label: 'Daily Impressions', value: space.daily_impressions ? `~${space.daily_impressions.toLocaleString()}` : 'N/A' },
    { icon: <Zap size={16} />, label: 'Format', value: space.is_digital ? 'Digital LED' : 'Static Print' },
    { icon: <Sun size={16} />, label: 'Illuminated', value: space.is_illuminated ? 'Yes' : 'No' },
    space.faces_direction ? { icon: <MapPin size={16} />, label: 'Facing', value: space.faces_direction } : null,
    { icon: <Calendar size={16} />, label: 'Min. Booking', value: `${space.minimum_booking_days || 7} days` },
  ].filter(Boolean)

  return (
    <DashboardLayout>
      {/* Back nav */}
      <div style={{ marginBottom: 20 }}>
        <Button variant="ghost" icon={<ArrowLeft size={16} />} onClick={() => navigate('/explore')}>Back to Explore</Button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 380px', gap: 28, alignItems: 'start' }}>
        {/* Left: Details */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
          <ImageGallery photos={space.photos} />

          {/* Header */}
          <div>
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16 }}>
              <div>
                <div style={{ display: 'flex', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
                  <Badge variant="navy" style={{ textTransform: 'capitalize' }}>{space.type?.replace('_', ' ')}</Badge>
                  {space.is_digital && <Badge variant="info">Digital</Badge>}
                  {space.is_available ? <Badge variant="success">Available</Badge> : <Badge variant="warning">Booked</Badge>}
                </div>
                <h1 style={{ fontFamily: 'var(--font-display)', fontSize: '1.6rem', fontWeight: 800, marginBottom: 6 }}>{space.title}</h1>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'var(--gray-500)', fontSize: '0.9rem' }}>
                  <MapPin size={14} /> {space.address || space.area}, {space.city}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={() => setSaved(!saved)} className="btn btn-outline btn-icon">
                  <Heart size={18} fill={saved ? 'var(--danger)' : 'none'} color={saved ? 'var(--danger)' : undefined} />
                </button>
                <button className="btn btn-outline btn-icon"><Share2 size={18} /></button>
              </div>
            </div>
          </div>

          {/* Description */}
          {space.description && (
            <div className="card" style={{ padding: 20 }}>
              <h3 style={{ fontFamily: 'var(--font-display)', marginBottom: 10 }}>About this Space</h3>
              <p style={{ color: 'var(--gray-600)', lineHeight: 1.7 }}>{space.description}</p>
            </div>
          )}

          {/* Specs */}
          <div className="card" style={{ padding: 20 }}>
            <h3 style={{ fontFamily: 'var(--font-display)', marginBottom: 16 }}>Specifications</h3>
            <div className="grid-2" style={{ gap: 16 }}>
              {specsData.map((spec, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', background: 'var(--gray-50)', borderRadius: 'var(--radius)' }}>
                  <div style={{ color: 'var(--tech)', flexShrink: 0 }}>{spec.icon}</div>
                  <div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--gray-400)', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600 }}>{spec.label}</div>
                    <div style={{ fontWeight: 700, color: 'var(--gray-800)' }}>{spec.value}</div>
                  </div>
                </div>
              ))}
            </div>

            {/* Audience tags */}
            {space.audience_type?.length > 0 && (
              <div style={{ marginTop: 16 }}>
                <div style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--gray-500)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Audience</div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {space.audience_type.map(a => <Badge key={a} variant="neutral" style={{ textTransform: 'capitalize' }}>{a}</Badge>)}
                </div>
              </div>
            )}
          </div>

          {/* Nearby landmarks */}
          {space.nearby_landmarks?.length > 0 && (
            <div className="card" style={{ padding: 20 }}>
              <h3 style={{ fontFamily: 'var(--font-display)', marginBottom: 12 }}>Nearby Landmarks</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {space.nearby_landmarks.map((l, i) => (
                  <div key={i} style={{ display: 'flex', gap: 8, color: 'var(--gray-600)', fontSize: '0.9rem' }}>
                    <MapPin size={14} style={{ color: 'var(--tech)', marginTop: 2, flexShrink: 0 }} /> {l}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Media owner card */}
          {owner && (
            <div className="card" style={{ padding: 20 }}>
              <h3 style={{ fontFamily: 'var(--font-display)', marginBottom: 14 }}>Media Owner</h3>
              <div style={{ display: 'flex', gap: 14, alignItems: 'center' }}>
                <Avatar name={owner.company_name || owner.full_name} size={48} />
                <div>
                  <div style={{ fontWeight: 700, fontSize: '1rem' }}>{owner.company_name || owner.full_name}</div>
                  <div style={{ fontSize: '0.82rem', color: 'var(--gray-400)', marginTop: 2 }}>
                    {owner.is_verified ? '✅ Verified Media Owner' : 'Media Owner'}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Right: Booking card */}
        <div style={{ position: 'sticky', top: 80 }}>
          <div className="card card-elevated" style={{ padding: 24 }}>
            {/* Pricing */}
            <div style={{ marginBottom: 20 }}>
              {space.price_per_month && (
                <div style={{ marginBottom: 4 }}>
                  <span style={{ fontFamily: 'var(--font-display)', fontSize: '2rem', fontWeight: 900, color: 'var(--navy)' }}>
                    {space.price_per_month.toLocaleString()}
                  </span>
                  <span style={{ color: 'var(--gray-400)', marginLeft: 6 }}>AED / month</span>
                </div>
              )}
              {space.price_per_week && (
                <div style={{ fontSize: '0.88rem', color: 'var(--gray-500)' }}>
                  or {space.price_per_week.toLocaleString()} AED / week
                </div>
              )}
              {space.price_per_day && (
                <div style={{ fontSize: '0.88rem', color: 'var(--gray-500)' }}>
                  or {space.price_per_day.toLocaleString()} AED / day
                </div>
              )}
              {space.production_fee > 0 && (
                <div style={{ marginTop: 8, fontSize: '0.82rem', color: 'var(--warning)', fontWeight: 500 }}>
                  + {space.production_fee.toLocaleString()} AED production fee
                </div>
              )}
            </div>

            {/* Key info */}
            <div style={{ background: 'var(--gray-50)', borderRadius: 'var(--radius)', padding: 14, marginBottom: 20 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8, fontSize: '0.85rem' }}>
                <span style={{ color: 'var(--gray-500)' }}>Min. booking</span>
                <span style={{ fontWeight: 600 }}>{space.minimum_booking_days || 7} days</span>
              </div>
              {space.daily_impressions && (
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem' }}>
                  <span style={{ color: 'var(--gray-500)' }}>Daily impressions</span>
                  <span style={{ fontWeight: 600, color: 'var(--tech)' }}>~{space.daily_impressions.toLocaleString()}</span>
                </div>
              )}
            </div>

            {space.is_available ? (
              <Button
                fullWidth size="lg"
                onClick={() => { if (!user) navigate('/login'); else setShowBooking(true) }}
                icon={<Calendar size={18} />}
              >
                Request Booking
              </Button>
            ) : (
              <Button fullWidth size="lg" variant="outline" disabled>Currently Booked</Button>
            )}

            <p style={{ fontSize: '0.78rem', color: 'var(--gray-400)', textAlign: 'center', marginTop: 12, lineHeight: 1.5 }}>
              📋 Free to inquire. Payment only after approval. AdsLocate charges 10% commission.
            </p>

            {/* Feature list */}
            <div style={{ marginTop: 20, paddingTop: 16, borderTop: '1px solid var(--gray-100)' }}>
              {[
                '✓ No payment until approved',
                '✓ Direct contact with owner',
                '✓ Artwork review included',
                '✓ Proof of posting required',
              ].map((f, i) => (
                <div key={i} style={{ fontSize: '0.82rem', color: 'var(--gray-500)', padding: '4px 0' }}>{f}</div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <BookingModal space={space} isOpen={showBooking} onClose={() => setShowBooking(false)} />
    </DashboardLayout>
  )
}
